import CREW_COLORS from '../Routing/TerritoryWorkbench/crewColors'
import { HMI_YARD, haversineMiles } from '../Routing/TerritoryWorkbench/mapUtils'

export const V4_T = {
  cream: '#f4efe4',
  surface: '#fbf8f1',
  surface2: '#faf6ee',
  line: '#e7dfc9',
  lineSoft: '#ede6d2',
  ink: '#1c2419',
  ink2: '#3d463a',
  mute: '#6f7a6b',
  muteSoft: '#97a094',
  green: '#1f5e3a',
  greenDeep: '#15422a',
  greenSoft: '#e3eee0',
  greenSofter: '#eef5eb',
  rust: '#a9341d',
  rustSoft: '#fbeee6',
  orange: '#c66a45',
  amber: '#d8973a',
  plum: '#6e3fb7',
  blue: '#3454c1',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  font: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
}

export const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

// Format number with decimals
export const formatNumber = (value, decimals = 2) => {
  if (value == null || isNaN(value)) return '-'
  return Number(value).toFixed(decimals)
}

export const formatVisitSplit = (mtcVisits, totalVisits) => {
  const mtc = Number(mtcVisits || 0)
  const total = Number(totalVisits || 0)
  return total > 0 && total !== mtc ? `${mtc} / ${total}` : `${mtc}`
}

let _warnedAddressObject = false
// Coerce an address value to a safe, renderable STRING. Property/jobsite address
// fields are normally strings, but some records arrive with a structured object
// (e.g. `{ street, city, state, country, zipCode }` from a geocode/Aspire payload
// stored in a flexible field) — rendering that object directly throws React's
// "Objects are not valid as a React child". This normalizes any shape and, the
// first time it sees an object, logs WHAT it got so the data source can be fixed.
export const formatAddress = (value) => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.filter(Boolean).map(formatAddress).join(', ')
  if (typeof value === 'object') {
    if (!_warnedAddressObject && typeof console !== 'undefined') {
      _warnedAddressObject = true
      // eslint-disable-next-line no-console
      console.warn('[formatAddress] received an object address (expected string); coercing. Value:', value)
    }
    const street = value.street || value.address || value.address1 || ''
    const city = value.city || ''
    const state = value.state || ''
    const zip = value.zipCode || value.zip || value.zipcode || ''
    const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    return [street, cityStateZip].filter(Boolean).join(', ')
  }
  return String(value)
}

// Format percentage
export const formatPercent = (value) => {
  if (value == null || isNaN(value)) return '-'
  return `${Number(value).toFixed(1)}%`
}

// Format date
export const formatDate = (dateStr) => {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export const formatShortDate = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return formatDateParts(parts)
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString()
}

export const formatShortDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return parts.hour == null ? formatDateParts(parts) : `${formatDateParts(parts)}, ${formatTimeParts(parts)}`
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return String(dateStr)
  return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

export const formatTimeOnly = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return formatTimeParts(parts)
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return String(dateStr)
  return formatTimeParts({
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes()
  })
}

export const getAspireDateTimeParts = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes()
    }
  }

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/)
  if (!match) return null
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: match[4] == null ? null : Number(match[4]),
    minute: match[5] == null ? null : Number(match[5])
  }
}

export const formatDateParts = ({ year, month, day }) => {
  if (!year || !month || !day) return '-'
  return new Date(year, month - 1, day).toLocaleDateString()
}

export const formatTimeParts = ({ hour, minute }) => {
  if (hour == null || minute == null) return '-'
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`
}

export const getCrewLabel = (crew = {}) => {
  const selectedCrew = crew || {}
  return selectedCrew.name || selectedCrew.crewLeaderName || '-'
}

export const getFirstPresent = (source = {}, keys = []) => {
  const row = source || {}
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key]
  }
  return null
}

export const formatCurrencyOrPending = (value) => {
  if (value == null || value === '') return 'Pending data'
  return formatCurrency(value)
}

export const getVisitDateValue = (visit = {}) => {
  return getFirstPresent(visit, [
    'date',
    'completedAt',
    'completedDate',
    'scheduledDate',
    'scheduledStartDate',
    'ScheduledStartDate',
    'ScheduledDate',
    'VisitDate',
    'ArrivalTime',
    'DepartureTime',
    'CompleteDate',
    'ApprovedDate',
    'startDate',
    'visitDate'
  ])
}

export const formatVisitDateTime = (visit = {}) => {
  const dateValue = getVisitDateValue(visit)
  if (!dateValue) return 'Date pending'

  const date = new Date(dateValue)
  const dateParts = getAspireDateTimeParts(dateValue)
  const dateLabel = dateParts ? formatDateParts(dateParts) : isNaN(date.getTime()) ? String(dateValue) : date.toLocaleDateString()
  const startTime = getFirstPresent(visit, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const endTime = getFirstPresent(visit, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])

  if (startTime && endTime) return `${dateLabel} · ${formatTimeOnly(startTime)}-${formatTimeOnly(endTime)}`
  if (startTime) return `${dateLabel} · ${formatTimeOnly(startTime)}`
  if (dateParts && dateParts.hour != null) {
    return `${dateLabel}, ${formatTimeParts(dateParts)}`
  }
  if (!isNaN(date.getTime()) && (date.getUTCHours() || date.getUTCMinutes())) {
    return `${dateLabel}, ${formatTimeOnly(date)}`
  }
  return dateLabel
}

export const getVisitCrewName = (visit = {}) => {
  return getFirstPresent(visit, [
    'activeRouteCrewLeaderName',
    'resolvedCrewLeaderName',
    'resolvedRouteCrewLeaderName',
    'routeCrewLeaderName',
    'CrewLeaderName',
    'crewLeaderName',
    'crewName',
    'foremanName',
    'assignedCrewName',
    'routeName',
    'RouteName',
    'PrimaryContactFullName',
    'ContactName'
  ]) || visit.crew?.name || 'Crew pending'
}

export const getRawVisit = (visit = {}) => visit?.raw && typeof visit.raw === 'object' ? visit.raw : visit

export const getWorkTicketId = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, ['workTicketId', 'WorkTicketID']) || getFirstPresent(visit, ['workTicketId', 'WorkTicketID'])
}

export const getWorkTicketNumber = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, ['workTicketNumber', 'WorkTicketNumber']) || getFirstPresent(visit, ['ticket'])
}

export const getWorkTicketVisitId = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, [
    'workTicketVisitId',
    'workTicketVisitID',
    'WorkTicketVisitID',
    'WorkTicketVisitId',
    'VisitID',
    'visitId'
  ])
}

export const getAspireWorkTicketUrl = (visit = {}) => {
  const workTicketId = getWorkTicketId(visit)
  return workTicketId ? `https://cloud.youraspire.com/app/worktickets/details/${workTicketId}` : null
}

export const getAspireWorkTicketVisitUrl = (visit = {}) => {
  const workTicketVisitId = getWorkTicketVisitId(visit)
  return workTicketVisitId ? `https://cloud.youraspire.com/app/worktickets/visit/${workTicketVisitId}` : null
}

export const getTicketLabel = (visit = {}) => {
  const ticketNumber = getWorkTicketNumber(visit)
  const ticketId = getWorkTicketId(visit)
  if (ticketNumber && ticketNumber !== '-') return `TX ${ticketNumber}`
  if (ticketId) return `Work Ticket ${ticketId}`
  return null
}

export const getVisitCrewCountForNormalization = (visit = {}) => {
  const row = getRawVisit(visit)
  const entries = getCrewTimeEntries(visit)
  const distinctClockContacts = new Set(
    entries
      .filter(entry => !entry.isDriveTime)
      .map(entry => getFirstPresent(entry, ['contactId', 'ContactID', 'employeeId', 'EmployeeID']))
      .filter(Boolean)
  )
  if (distinctClockContacts.size > 1) return distinctClockContacts.size

  const actualCrewCount = safeNumber(getFirstPresent(row, ['actualCrewCount', 'ActualCrewCount']) || getFirstPresent(visit, ['actualCrewCount', 'ActualCrewCount']), 0)
  if (actualCrewCount > 0) return actualCrewCount

  const routeCrewCount = safeNumber(getFirstPresent(row, [
    'activeRouteCrewSize',
    'routeCrewSize',
    'resolvedRouteCrewSize',
    'RouteSize',
    'routeSize'
  ]) || getFirstPresent(visit, [
    'activeRouteCrewSize',
    'routeCrewSize',
    'resolvedRouteCrewSize',
    'RouteSize',
    'routeSize'
  ]), 0)
  if (routeCrewCount > 0 && routeCrewCount <= 12) return routeCrewCount

  const scheduledCrewCount = safeNumber(getFirstPresent(row, ['CrewMembers', 'crewMembers']) || getFirstPresent(visit, ['CrewMembers', 'crewMembers']), 0)
  if (scheduledCrewCount > 0 && scheduledCrewCount <= 2) return scheduledCrewCount

  return 3
}

export const getCrewHourFallback = (visit = {}) => {
  const row = getRawVisit(visit)
  const onSiteCrewHours = safeNumber(getFirstPresent(row, [
    'actualOnSiteHours',
    'ActualOnSiteHours',
    'OnSiteHours',
    'TicketOnSiteHours'
  ]), 0)
  if (onSiteCrewHours <= 0) return 0

  const crewCount = getVisitCrewCountForNormalization(visit)
  return crewCount > 1 ? onSiteCrewHours / crewCount : onSiteCrewHours
}

export const getVisitHours = (visit = {}) => {
  const clockDuration = getVisitClockDurationHours(visit)
  if (clockDuration > 0) return clockDuration

  const crewHourFallback = getCrewHourFallback(visit)
  if (crewHourFallback > 0) return crewHourFallback

  const rawManHours = safeNumber(getFirstPresent(visit, [
    'HoursAct',
    'Hours',
    'hours',
    'HoursEst',
    'estimatedManHours'
  ]), 0)
  if (rawManHours > 0) {
    const crewCount = getVisitCrewCountForNormalization(visit)
    return crewCount > 1 ? rawManHours / crewCount : rawManHours
  }

  return getFirstPresent(visit, ['estimatedHours'])
}

export const getVisitStatus = (visit = {}) => getFirstPresent(visit, [
  'WorkTicketStatusName',
  'WorkTicketStatus',
  'statusName',
  'status'
])

export const getCrewTimeEntries = (visit = {}) => {
  const row = getRawVisit(visit)
  const entries = row.crewTimeEntries || row.CrewTimeEntries || visit.crewTimeEntries || []
  return Array.isArray(entries) ? entries : []
}

export const getClockEntryName = (entry = {}) => {
  return getFirstPresent(entry, [
    'contactName',
    'ContactName',
    'crewMemberName',
    'CrewMemberName',
    'employeeName',
    'EmployeeName',
    'name',
    'Name'
  ]) || [entry.firstName || entry.FirstName, entry.lastName || entry.LastName].filter(Boolean).join(' ')
}

export const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Stable color picker so each crew member gets a consistent chip color
// across visits.
const PEOPLE_CHIP_COLORS = CREW_COLORS
export const colorForName = (name = '') => {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return PEOPLE_CHIP_COLORS[Math.abs(hash) % PEOPLE_CHIP_COLORS.length]
}

// Aspire time-entry URL for a visit: jumps to the daily time-entry detail page
// for the work ticket on the visit's completed/scheduled date.
// Pattern: https://cloud.youraspire.com/app/timeentry/details/YYYY-MM-DD/0/0/<WorkTicketID>
export const getAspireTimeEntryUrl = (visit = {}) => {
  const raw = getRawVisit(visit)
  const dateValue = getVisitDateValue(raw) || getVisitDateValue(visit)
  const ticketId = getWorkTicketId(raw) || getWorkTicketId(visit)
  if (!dateValue || !ticketId) return null
  // If the source already looks like a YYYY-MM-DD prefix, trust it verbatim —
  // Aspire-issued visit dates are calendar dates, not timezone-bearing instants,
  // so any timezone interpretation can shift them by a day.
  const dateStr = String(dateValue)
  const prefixMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
  let yyyy; let mm; let dd
  if (prefixMatch) {
    [, yyyy, mm, dd] = prefixMatch
  } else {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return null
    yyyy = date.getUTCFullYear()
    mm = String(date.getUTCMonth() + 1).padStart(2, '0')
    dd = String(date.getUTCDate()).padStart(2, '0')
  }
  return `https://cloud.youraspire.com/app/timeentry/details/${yyyy}-${mm}-${dd}/0/0/${ticketId}`
}

export const getVisitPeopleChips = (visit = {}) => {
  const entries = getCrewTimeEntries(visit)
  // Aggregate OT hours per person (a person can have multiple clock entries on
  // one visit). Keyed by clocked name so it lines up with the chip rows.
  const otByName = new Map()
  entries.forEach(entry => {
    const name = getClockEntryName(entry)
    if (!name) return
    const ot = safeNumber(entry.otHours ?? entry.OTHours)
    if (ot > 0) otByName.set(name, (otByName.get(name) || 0) + ot)
  })

  const names = entries.map(getClockEntryName).filter(Boolean)
  const uniqueNames = [...new Set(names)]
  if (!uniqueNames.length) {
    const fallback = visit.crew || getVisitCrewName(getRawVisit(visit))
    return { chips: [], fallback: fallback || null, overflow: 0 }
  }
  // Show every clocked-in crew member by default. The legacy "+N" overflow is
  // kept as a guardrail for pathological visits with very large crews.
  const SHOW = 12
  const chips = uniqueNames.slice(0, SHOW).map(name => ({
    name,
    initials: getInitials(name),
    color: colorForName(name),
    otHours: Math.round((otByName.get(name) || 0) * 100) / 100
  }))
  return { chips, fallback: null, overflow: Math.max(0, uniqueNames.length - SHOW) }
}

// Visit-level overtime rollup. Prefers the server-computed visitOTHours/visitOTCost
// (summed from clock entries in the visit-history endpoint); falls back to summing
// the raw crew time entries if those aren't present.
export const getVisitOT = (visit = {}) => {
  const row = getRawVisit(visit)
  const directHours = safeNumber(getFirstPresent(visit, ['visitOTHours']) ?? getFirstPresent(row, ['visitOTHours']))
  const directCost = safeNumber(getFirstPresent(visit, ['visitOTCost']) ?? getFirstPresent(row, ['visitOTCost']))
  if (directHours > 0 || directCost > 0) return { otHours: directHours, otCost: directCost }

  const entries = getCrewTimeEntries(visit)
  const otHours = entries.reduce((sum, entry) => sum + safeNumber(entry.otHours ?? entry.OTHours), 0)
  const otCost = entries.reduce((sum, entry) => sum + safeNumber(entry.otCost ?? entry.OTLaborCost), 0)
  return { otHours: Math.round(otHours * 100) / 100, otCost: Math.round(otCost * 100) / 100 }
}

export const getVisitTimeRange = (visit = {}) => {
  const row = getRawVisit(visit)
  const start = getFirstPresent(row, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const end = getFirstPresent(row, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])
  if (start || end) return `${start ? formatTimeOnly(start) : '-'}-${end ? formatTimeOnly(end) : '-'}`

  const entries = getCrewTimeEntries(visit)
  const starts = entries.map(entry => getFirstPresent(entry, ['startTime', 'StartTime'])).filter(Boolean)
  const ends = entries.map(entry => getFirstPresent(entry, ['endTime', 'EndTime'])).filter(Boolean)
  if (!starts.length && !ends.length) return null

  const startDates = starts.map(value => new Date(value)).filter(date => !isNaN(date.getTime()))
  const endDates = ends.map(value => new Date(value)).filter(date => !isNaN(date.getTime()))
  const firstStart = startDates.length ? new Date(Math.min(...startDates.map(date => date.getTime()))) : starts[0]
  const lastEnd = endDates.length ? new Date(Math.max(...endDates.map(date => date.getTime()))) : ends[ends.length - 1]
  return `${firstStart ? formatTimeOnly(firstStart) : '-'}-${lastEnd ? formatTimeOnly(lastEnd) : '-'}`
}

export const getTimestampMs = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return isNaN(date.getTime()) ? null : date.getTime()
}

export const getDurationHours = (start, end) => {
  const startMs = getTimestampMs(start)
  const endMs = getTimestampMs(end)
  if (startMs == null || endMs == null || endMs <= startMs) return null
  return (endMs - startMs) / (60 * 60 * 1000)
}

export const getEntryHours = (entry = {}) => safeNumber(getFirstPresent(entry, ['hours', 'Hours', 'TotalHours']), 0)

export const averagePositive = (values = []) => {
  const positive = values.map(value => safeNumber(value, 0)).filter(value => value > 0)
  if (!positive.length) return 0
  return positive.reduce((sum, value) => sum + value, 0) / positive.length
}

export const getVisitClockDurationHours = (visit = {}) => {
  const row = getRawVisit(visit)
  const start = getFirstPresent(row, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const end = getFirstPresent(row, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])
  const directDuration = getDurationHours(start, end)
  if (directDuration) return directDuration

  const onSiteEntries = getCrewTimeEntries(visit).filter(entry => !entry.isDriveTime)
  const starts = onSiteEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['startTime', 'StartTime']))).filter(value => value != null)
  const ends = onSiteEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['endTime', 'EndTime']))).filter(value => value != null)
  if (starts.length && ends.length) {
    const entryDuration = (Math.max(...ends) - Math.min(...starts)) / (60 * 60 * 1000)
    if (entryDuration > 0) return entryDuration
  }

  return averagePositive(onSiteEntries.map(getEntryHours))
}

export const getVisitActualHours = (visit = {}) => {
  const clockDuration = getVisitClockDurationHours(visit)
  if (clockDuration > 0) return clockDuration

  const crewHourFallback = getCrewHourFallback(visit)
  if (crewHourFallback > 0) return crewHourFallback

  const rawActualManHours = safeNumber(getFirstPresent(visit, [
    'HoursAct',
    'actualHours',
    'ActualHours'
  ]), 0)
  if (rawActualManHours > 0) {
    const crewCount = getVisitCrewCountForNormalization(visit)
    return crewCount > 1 ? rawActualManHours / crewCount : rawActualManHours
  }

  return 0
}

// Associated drive time for a visit: HoursAct - OnSiteHours, as reported by
// Aspire. Returns 0 when either field is missing or the math goes negative
// (clock skew, partial timecards, etc.).
export const getVisitAssociatedDriveHours = (visit = {}) => {
  const row = getRawVisit(visit)
  const hoursAct = safeNumber(getFirstPresent(row, ['HoursAct', 'hoursAct']) ?? getFirstPresent(visit, ['HoursAct', 'hoursAct']), 0)
  const onSite = safeNumber(getFirstPresent(row, ['OnSiteHours', 'onSiteHours', 'ActualOnSiteHours', 'actualOnSiteHours', 'TicketOnSiteHours']) ?? getFirstPresent(visit, ['OnSiteHours', 'onSiteHours', 'ActualOnSiteHours', 'actualOnSiteHours', 'TicketOnSiteHours']), 0)
  if (hoursAct <= 0 || onSite <= 0) return 0
  return Math.max(0, hoursAct - onSite)
}

export const getVisitDriveDurationHours = (visit = {}) => {
  const driveEntries = getCrewTimeEntries(visit).filter(entry => entry.isDriveTime)
  if (!driveEntries.length) return 0

  const starts = driveEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['startTime', 'StartTime']))).filter(value => value != null)
  const ends = driveEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['endTime', 'EndTime']))).filter(value => value != null)
  if (starts.length && ends.length) {
    const entryDuration = (Math.max(...ends) - Math.min(...starts)) / (60 * 60 * 1000)
    if (entryDuration > 0) return entryDuration
  }

  return averagePositive(driveEntries.map(getEntryHours))
}

export const getMedian = (values = []) => {
  const sorted = values.map(value => safeNumber(value, 0)).filter(value => value > 0).sort((a, b) => a - b)
  if (!sorted.length) return 0
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

export const getVisitTrendStats = (visits = [], fallbackAvg = 0) => {
  const recentVisits = visits.filter(visit => visit.wAgo < 4)
  const priorVisits = visits.filter(visit => visit.wAgo >= 4 && visit.wAgo < 12)
  const recentAvg = recentVisits.length
    ? recentVisits.reduce((sum, visit) => sum + visit.hrs, 0) / recentVisits.length
    : fallbackAvg
  const priorAvg = priorVisits.length
    ? priorVisits.reduce((sum, visit) => sum + visit.hrs, 0) / priorVisits.length
    : recentAvg

  return {
    recentAvg,
    trendPct: priorAvg ? Math.round(((recentAvg - priorAvg) / priorAvg) * 100) : 0
  }
}

export const filterVisitRowsByType = (visits = [], typeFilter = 'all') => {
  if (!typeFilter || typeFilter === 'all') return visits
  return visits.filter(visit => visit.type === typeFilter)
}

export const getMaintenanceMetricRows = (visitRows = []) => {
  const actualRows = visitRows.filter(visit => !visit.isEstimated && safeNumber(visit.actualHrs ?? visit.hrs, 0) > 0)
  const maintenanceRows = actualRows.filter(visit => visit.type === 'Maint')
  return maintenanceRows.length ? maintenanceRows : actualRows
}

export const safeNumber = (value, fallback = 0) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

export const pickNumber = (source = {}, keys = [], fallback = 0) => {
  const value = getFirstPresent(source, keys)
  return value === null ? fallback : safeNumber(value, fallback)
}

export const formatCompactCurrency = (value) => {
  const number = safeNumber(value)
  if (Math.abs(number) >= 1000) return `$${(number / 1000).toFixed(1)}k`
  return formatCurrency(number)
}

export const getDayAbbrev = (day) => {
  const value = String(day || '').trim()
  if (!value) return '-'
  return value.slice(0, 3).toUpperCase()
}

export const getServiceTypeKey = (visit = {}) => {
  const service = String(
    getFirstPresent(visit, ['serviceName', 'ServiceName', 'ServiceDisplayName', 'serviceCategory', 'ServiceCategory']) || ''
  ).toLowerCase()
  if (service.includes('irrig')) return 'Irrig'
  if (service.includes('herb')) return 'Herb'
  if (service.includes('maint')) return 'Maint'
  return 'Other'
}

export const getVisitServiceLabel = (visit = {}) => {
  return getFirstPresent(visit, [
    'OpportunityName',
    'opportunityName',
    'ServiceDisplayName',
    'serviceDisplayName',
    'ServiceName',
    'serviceName'
  ])
}

export const getVisitDate = (visit = {}) => {
  const value = getVisitDateValue(visit)
  const date = value ? new Date(value) : null
  return date && !isNaN(date.getTime()) ? date : null
}

export const getWeeksAgo = (date, now = new Date()) => {
  if (!date) return 0
  return Math.max(0, Math.round((now.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000)))
}

export const buildVisitChartRows = (selectedProperty, detailVisits = []) => {
  const sourceVisits = detailVisits.length ? detailVisits : getVisitHistoryRows(selectedProperty)
  const now = new Date()
  const mapped = sourceVisits
    .map((visit, index) => {
      const actualHrs = safeNumber(getVisitActualHours(visit), 0)
      const hrs = actualHrs || safeNumber(getVisitHours(visit), 0)
      const date = getVisitDate(visit)
      const dateValue = getVisitDateValue(visit)
      const type = getServiceTypeKey(visit)
      const isEstimated = actualHrs <= 0 && hrs > 0
      return {
        id: getWorkTicketId(visit) || getWorkTicketNumber(visit) || `${date?.toISOString() || 'visit'}-${index}`,
        wAgo: getWeeksAgo(date, now),
        hrs,
        actualHrs,
        isEstimated,
        type,
        label: type === 'Other' ? getVisitServiceLabel(visit) : null,
        crew: getVisitCrewName(visit),
        ticket: getWorkTicketNumber(visit) || getWorkTicketId(visit) || '-',
        date: dateValue ? formatShortDate(dateValue) : formatVisitDateTime(visit),
        status: getVisitStatus(visit),
        flag: !isEstimated && hrs > safeNumber(selectedProperty?.estimatedHours, 0) && safeNumber(selectedProperty?.estimatedHours, 0) > 0 ? 'high' : null,
        raw: visit
      }
    })
    .filter(row => row.hrs > 0)

  // Dedupe by work-ticket id: server payload sometimes has one entry per
  // crewTimeEntry/WorkTicketTime row, which surfaces as duplicate visit rows
  // here. Keep the entry with the most actual hours per ticket; on a tie,
  // prefer the more specific service type (anything but "Other").
  const TYPE_PRIORITY = { Maint: 3, Irrig: 3, Herb: 3, Other: 0 }
  const byTicket = new Map()
  for (const row of mapped) {
    const key = row.id
    const incumbent = byTicket.get(key)
    if (!incumbent) { byTicket.set(key, row); continue }
    const incHrs = incumbent.hrs
    const newHrs = row.hrs
    if (newHrs > incHrs) byTicket.set(key, row)
    else if (newHrs === incHrs && (TYPE_PRIORITY[row.type] || 0) > (TYPE_PRIORITY[incumbent.type] || 0)) {
      byTicket.set(key, row)
    }
  }
  const rows = Array.from(byTicket.values()).sort((a, b) => a.wAgo - b.wAgo)

  if (rows.length) return rows

  const avgHours = safeNumber(selectedProperty?.avgOnSiteHoursDisplay || selectedProperty?.avgOnSiteHours)
  return Array.from({ length: Math.min(safeNumber(selectedProperty?.completedOccurrences, 12), 12) || 6 }, (_, index) => ({
    id: `fallback-${index}`,
    wAgo: index,
    hrs: avgHours || safeNumber(selectedProperty?.estimatedHours, 1),
    actualHrs: 0,
    isEstimated: true,
    type: index % 5 === 0 ? 'Irrig' : 'Maint',
    crew: getCrewLabel(selectedProperty?.primaryCrewLeader),
    ticket: '-',
    date: index === 0 ? 'Recent' : `${index}w ago`,
    status: 'Complete',
    flag: null,
    raw: {}
  }))
}

export const buildMonthlyRows = (selectedProperty, visitRows = []) => {
  const monthRows = []
  const now = new Date()
  const ticketRows = Array.isArray(selectedProperty?.workTickets) ? selectedProperty.workTickets : []
  const hasTicketFinancials = ticketRows.some(ticket =>
    getFirstPresent(ticket, ['EarnedRevenue', 'earnedRevenue', 'LaborCostAct', 'laborCostAct']) !== null
  )
  const fallbackRevenue = pickNumber(selectedProperty, ['maintenanceEarnedRevenue', 'totalEarnedRevenue', 'totalRevenue'])
  const fallbackLaborCost = pickNumber(selectedProperty, ['maintenanceLaborCost', 'totalLaborCost'])

  for (let index = 11; index >= 0; index -= 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - index, 1)
    const key = `${monthDate.getFullYear()}-${monthDate.getMonth()}`
    const visits = visitRows.filter(visit => {
      const date = getVisitDate(visit.raw)
      return date && `${date.getFullYear()}-${date.getMonth()}` === key
    })
    const tickets = hasTicketFinancials
      ? ticketRows.filter(ticket => {
          const date = getVisitDate(ticket)
          return date && `${date.getFullYear()}-${date.getMonth()}` === key
        })
      : []
    const visitCount = visits.length
    const actualHours = visitCount
      ? visits.reduce((sum, visit) => sum + visit.hrs, 0) / visitCount
      : safeNumber(selectedProperty?.avgOnSiteHoursDisplay || selectedProperty?.avgOnSiteHours)
    const monthlyRevenue = hasTicketFinancials
      ? tickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['EarnedRevenue', 'earnedRevenue']), 0)
      : fallbackRevenue / 12
    const monthlyCost = hasTicketFinancials
      ? tickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['LaborCostAct', 'laborCostAct']), 0)
      : fallbackLaborCost / 12
    monthRows.push({
      key,
      short: monthDate.toLocaleString([], { month: 'short' }).slice(0, 1),
      label: monthDate.toLocaleString([], { month: 'short' }),
      est: safeNumber(selectedProperty?.estimatedHours),
      act: actualHours,
      rev: monthlyRevenue,
      othRev: 0,
      cost: monthlyCost,
      visits: visitCount || tickets.length || (!hasTicketFinancials ? Math.max(0, Math.round(safeNumber(selectedProperty?.completedOccurrences) / 12)) : 0),
      current: index === 0
    })
  }

  return monthRows
}

// Canonical financial-period helpers (verbatim port from RoutingPropertiesTable.js; fork copy pending delete).
export const FINANCIAL_PERIOD_OPTIONS = [
  { value: 'ytd', label: 'YTD' },
  { value: 'trailing12', label: 'Trailing 12' },
  { value: 'lifetime', label: 'Lifetime' }
]

const getMonthKey = (date) => `${date.getFullYear()}-${date.getMonth()}`

const getMonthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1)

const addMonths = (date, offset) => new Date(date.getFullYear(), date.getMonth() + offset, 1)

const getFinancialPeriodMeta = (period) => {
  if (period === 'trailing12') return { shortLabel: 'Trailing 12', chartLabel: 'trailing 12 by month', detailLabel: 'trailing 12 months' }
  if (period === 'lifetime') return { shortLabel: 'Lifetime', chartLabel: 'lifetime trend', detailLabel: 'lifetime' }
  return { shortLabel: 'YTD', chartLabel: 'YTD by month', detailLabel: 'year to date' }
}

const getFinancialPeriodBounds = (period, now = new Date()) => {
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
  if (period === 'trailing12') {
    return { start: new Date(now.getFullYear(), now.getMonth() - 11, 1), end }
  }
  if (period === 'lifetime') {
    return { start: null, end: null }
  }
  return { start: new Date(now.getFullYear(), 0, 1), end }
}

const getFinancialTicketKind = (ticket = {}) => {
  const type = getServiceTypeKey(ticket)
  return type === 'Irrig' || type === 'Herb' ? 'other' : 'maintenance'
}

const hasTicketFinancialValue = (ticket = {}) => (
  getFirstPresent(ticket, ['EarnedRevenue', 'earnedRevenue', 'LaborCostAct', 'laborCostAct', 'HourCostEst', 'hourCostEst', 'LaborCostEst', 'laborCostEst']) !== null
)

const getTicketEstimatedLabor = (ticket = {}) => pickNumber(ticket, ['HourCostEst', 'hourCostEst', 'LaborCostEst', 'laborCostEst', 'EstimatedLaborCost', 'estimatedLaborCost'])

const isDateInRange = (date, start, end) => {
  if (!date) return false
  if (start && date < start) return false
  if (end && date > end) return false
  return true
}

const buildFinancialMonthRows = (property, tickets, period, periodTickets, now = new Date(), perVisitContractRev = 0) => {
  const datedTickets = tickets
    .map(ticket => ({ ticket, date: getVisitDate(ticket) }))
    .filter(item => item.date)
    .sort((a, b) => a.date - b.date)
  let start
  let end
  if (period === 'lifetime') {
    start = datedTickets[0]?.date ? getMonthStart(datedTickets[0].date) : new Date(now.getFullYear(), now.getMonth(), 1)
    end = getMonthStart(now)
  } else {
    const bounds = getFinancialPeriodBounds(period, now)
    start = bounds.start
    end = getMonthStart(bounds.end)
  }

  const months = []
  for (let cursor = getMonthStart(start); cursor <= end; cursor = addMonths(cursor, 1)) {
    months.push(new Date(cursor))
  }
  const selectedTicketSet = new Set(periodTickets)
  const monthsWithTickets = period === 'lifetime'
    ? months.filter(monthDate => {
        const key = getMonthKey(monthDate)
        return tickets.some(ticket => selectedTicketSet.has(ticket) && getVisitDate(ticket) && getMonthKey(getVisitDate(ticket)) === key)
      })
    : months
  const visibleMonths = period === 'lifetime' && monthsWithTickets.length > 12 ? monthsWithTickets.slice(-12) : monthsWithTickets
  const monthRows = visibleMonths.map(monthDate => {
    const key = getMonthKey(monthDate)
    const monthTickets = tickets.filter(ticket => {
      if (!selectedTicketSet.has(ticket)) return false
      const date = getVisitDate(ticket)
      return date && getMonthKey(date) === key
    })
    const monthMaintenanceTickets = monthTickets.filter(ticket => getFinancialTicketKind(ticket) === 'maintenance')
    const ticketMaintRevenue = monthMaintenanceTickets
      .reduce((sum, ticket) => sum + pickNumber(ticket, ['EarnedRevenue', 'earnedRevenue']), 0)
    // Match buildFinancialPeriodData: when tickets carry no booked revenue,
    // allocate the per-visit contracted revenue so the chart bars aren't flat-zero.
    const maintenanceRevenue = ticketMaintRevenue > 0
      ? ticketMaintRevenue
      : perVisitContractRev * monthMaintenanceTickets.length
    const otherRevenue = monthTickets
      .filter(ticket => getFinancialTicketKind(ticket) === 'other')
      .reduce((sum, ticket) => sum + pickNumber(ticket, ['EarnedRevenue', 'earnedRevenue']), 0)
    return {
      key,
      short: monthDate.toLocaleString([], { month: 'short' }).slice(0, 1),
      label: monthDate.toLocaleString([], { month: 'short' }),
      est: safeNumber(property?.estimatedHours),
      act: 0,
      rev: maintenanceRevenue,
      othRev: otherRevenue,
      cost: monthTickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['LaborCostAct', 'laborCostAct']), 0),
      visits: monthTickets.length,
      current: getMonthKey(monthDate) === getMonthKey(now)
    }
  })

  return monthRows.length ? monthRows : buildMonthlyRows(property, [])
}

export const buildFinancialPeriodData = (sidebarData, period) => {
  const property = sidebarData.property || {}
  const base = sidebarData.kpis
  const tickets = (Array.isArray(property.workTickets) ? property.workTickets : []).filter(hasTicketFinancialValue)
  const meta = getFinancialPeriodMeta(period)
  if (!tickets.length) {
    return {
      meta,
      kpis: base,
      monthlyRows: sidebarData.monthlyRows
    }
  }

  const now = new Date()
  const { start, end } = getFinancialPeriodBounds(period, now)
  const periodTickets = tickets.filter(ticket => {
    const date = getVisitDate(ticket)
    if (period === 'lifetime' && !date) return true
    return isDateInRange(date, start, end)
  })
  const allDatedTickets = tickets.filter(ticket => getVisitDate(ticket))
  const datedPeriodTickets = periodTickets.filter(ticket => getVisitDate(ticket))
  const estimatedLaborFromTickets = periodTickets.reduce((sum, ticket) => sum + getTicketEstimatedLabor(ticket), 0)
  const estimateScale = allDatedTickets.length ? datedPeriodTickets.length / allDatedTickets.length : 1
  const estLabor = estimatedLaborFromTickets || (period === 'lifetime' ? base.estLabor : base.estLabor * estimateScale)
  // The server only sends MAINTENANCE tickets, but ships per-service aggregates
  // for irrigation/herbicide ("other"). The tab is all-service: maintenance is
  // ticket-driven (real dates), and "other" is prorated to the period by the
  // same visit fraction as maintenance (estimateScale; 1.0 for lifetime), since
  // other-service visits track roughly the same calendar.
  const otherScale = period === 'lifetime' ? 1 : estimateScale

  const maintenancePeriodTickets = periodTickets.filter(ticket => getFinancialTicketKind(ticket) === 'maintenance')
  const ticketEarnedRev = maintenancePeriodTickets
    .reduce((sum, ticket) => sum + pickNumber(ticket, ['EarnedRevenue', 'earnedRevenue']), 0)
  // Aspire books revenue at the contract/opportunity level, so per-ticket
  // EarnedRevenue is typically ~$0. When the tickets carry no booked revenue,
  // allocate the property's annual MAINTENANCE contracted revenue per visit so
  // each period shows a meaningful, period-aware figure (visits-in-period ×
  // per-visit rate). Real ticket revenue, when present, always wins. Falls back
  // to the all-service total when no maintenance-only figure is available.
  const annualMaintRev = getFirstPresent(property, ['maintenanceEstimatedRevenue']) === null
    ? safeNumber(property.totalEstimatedRevenue)
    : safeNumber(property.maintenanceEstimatedRevenue)
  const annualVisits = safeNumber(base && base.annualVisits)
  const perVisitContractRev = annualVisits > 0 ? annualMaintRev / annualVisits : 0
  const earnedRev = ticketEarnedRev > 0
    ? ticketEarnedRev
    : perVisitContractRev * maintenancePeriodTickets.length

  // Other (irrigation + herbicide) revenue. Real other-service tickets in the
  // period win (same precedence as maintenance) so the KPI matches the chart
  // bars, which are ticket-driven; otherwise fall back to the per-service
  // aggregate (booked earned, else estimate), prorated by the period fraction.
  const otherTicketEarnedRev = periodTickets
    .filter(ticket => getFinancialTicketKind(ticket) === 'other')
    .reduce((sum, ticket) => sum + pickNumber(ticket, ['EarnedRevenue', 'earnedRevenue']), 0)
  const otherAnnualRev = safeNumber(property.otherEarnedRevenue) > 0
    ? safeNumber(property.otherEarnedRevenue)
    : safeNumber(property.otherEstimatedRevenue)
  const othRev = otherTicketEarnedRev > 0
    ? otherTicketEarnedRev
    : otherAnnualRev * otherScale

  // All-service labor: maintenance ticket actuals + prorated other-service labor.
  const otherLaborAnnual = getFirstPresent(property, ['otherLaborCost']) === null
    ? safeNumber(property.irrigationLaborCost) + safeNumber(property.herbicideLaborCost)
    : safeNumber(property.otherLaborCost)
  const maintenanceActualLabor = periodTickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['LaborCostAct', 'laborCostAct']), 0)
  const actualLabor = maintenanceActualLabor + otherLaborAnnual * otherScale
  const totalRev = earnedRev + othRev
  const costVariance = estLabor - actualLabor
  // All-service net + margin: total revenue (maint + other) vs all-service labor.
  const netProfit = totalRev - actualLabor
  const margin = totalRev > 0 ? (netProfit / totalRev) * 100 : 0

  return {
    meta,
    kpis: {
      ...base,
      earnedRev,
      othRev,
      totalRev,
      estLabor,
      actualLabor,
      costVariance,
      netProfit,
      margin
    },
    monthlyRows: buildFinancialMonthRows(property, tickets, period, periodTickets, now, perVisitContractRev)
  }
}

export const buildServiceRows = (selectedProperty = {}) => {
  const irrigation = getServiceDetail(selectedProperty, 'Irrigation')
  const herbicide = getServiceDetail(selectedProperty, 'Herbicide')
  // completedOccurrences / futureTicketCount / totalOnSiteHours are already
  // MAINTENANCE-ONLY on the server (irrigation & herbicide tickets are split out
  // into their own per-service fields during sync). Do NOT subtract irr/herb
  // again here — that double-counts and under-states maintenance (e.g. 12 mtc
  // visits became 4), which then inflates deriveProfitability's annualization
  // factor ~3×. Only totalLaborCost is ALL-service, so its subtraction stays.
  const maintenanceCompleted = Math.max(0, safeNumber(selectedProperty.completedOccurrences))
  const maintenanceFuture = Math.max(0, safeNumber(selectedProperty.futureTicketCount || selectedProperty.futureVisits?.length))
  const maintenanceHours = Math.max(0, safeNumber(selectedProperty.totalOnSiteHours))
  const maintenanceCost = Math.max(0, safeNumber(selectedProperty.totalLaborCost) - safeNumber(irrigation.cost) - safeNumber(herbicide.cost))
  // Maintenance MAN-hours come straight from the server's maintenance-only
  // totalOnSiteManHours (it is NOT all-service, so do NOT subtract irr/herb).
  const maintenanceManHours = safeNumber(selectedProperty.totalOnSiteManHours)
  const rows = [
    {
      name: 'Irrigation',
      tickets: irrigation.tickets,
      completed: irrigation.completed,
      future: irrigation.future,
      hours: safeNumber(irrigation.hours),
      manHours: safeNumber(irrigation.manHours),
      cost: irrigation.cost
    },
    {
      name: 'Herbicide',
      tickets: herbicide.tickets,
      completed: herbicide.completed,
      future: herbicide.future,
      hours: safeNumber(herbicide.hours),
      manHours: safeNumber(herbicide.manHours),
      cost: herbicide.cost
    },
    {
      name: 'Maintenance',
      tickets: maintenanceCompleted + maintenanceFuture,
      completed: maintenanceCompleted,
      future: maintenanceFuture,
      hours: maintenanceHours,
      manHours: maintenanceManHours,
      cost: maintenanceCost
    }
  ].filter(service => service.tickets > 0 || service.hours > 0)

  const totalHours = rows.reduce((sum, service) => sum + service.hours, 0) || 1
  return rows.map(service => ({
    ...service,
    share: service.hours / totalHours
  }))
}

export const inferFrequencyFromTicketCount = (ticketCount = 0) => {
  const count = safeNumber(ticketCount)
  if (count <= 0) return 'none'
  if (count >= 36) return 'weekly'
  if (count >= 18) return 'biweekly'
  if (count >= 8) return 'monthly'
  if (count >= 3) return 'quarterly'
  if (count >= 2) return 'semiannual'
  return 'annual'
}

export const getPropertyServiceTicketCounts = (property = {}) => {
  const irrigationCompleted = safeNumber(property.irrigationCompletedVisits)
  const irrigationFuture = safeNumber(property.irrigationFutureVisits)
  const herbicideCompleted = safeNumber(property.herbicideCompletedVisits)
  const herbicideFuture = safeNumber(property.herbicideFutureVisits)
  const completedTotal = safeNumber(property.completedOccurrences)
  const futureTotal = safeNumber(property.futureTicketCount || property.futureVisits?.length)

  return {
    maintenance: Math.max(0, completedTotal + futureTotal - irrigationCompleted - irrigationFuture - herbicideCompleted - herbicideFuture),
    irrigation: irrigationCompleted + irrigationFuture,
    herbicide: herbicideCompleted + herbicideFuture
  }
}

export const inferPropertyEditDefaults = (property = {}) => {
  const counts = getPropertyServiceTicketCounts(property)
  return {
    mtcFrequency: property.mtcFrequency || inferFrequencyFromTicketCount(counts.maintenance),
    irrigationCheckFrequency: property.irrigationCheckFrequency || inferFrequencyFromTicketCount(counts.irrigation),
    herbicideApplicationFrequency: property.herbicideApplicationFrequency || inferFrequencyFromTicketCount(counts.herbicide),
    noIrrigation: property.noIrrigation != null ? !!property.noIrrigation : counts.irrigation <= 0
  }
}

// Route-first: show the current route's crew leader, never a historical one.
// 1) primaryCrewLeader (derived server-side from routed future visits)
// 2) the next future visit that carries a crew leader (also route-derived)
// 3) the next future visit that carries a route name
// Deliberately does NOT fall back to assignedCrewLeaders[0]/crewLeaders[0] —
// crewLeaders[] is the historical list of everyone who has ever worked the
// property, so falling back to it surfaces a stale leader (e.g. an old "Julio")
// as if it were the current assignment while the table correctly shows
// Unassigned.
//
// getPropertyRoute is the single source of truth for the property→route/crew
// label across the routing table, the HMI properties table, and the detail
// drawer chip. It returns null when nothing route-derived resolves, so callers
// that build filter/sort/dropdown values can drop Unassigned with Boolean
// filtering. getPrimaryCrewName is the drawer-facing wrapper that renders the
// same value but substitutes '-' for the Unassigned case.
export const getPropertyRoute = (property = {}) => {
  // Route-first: prefer the Aspire route name (the routing table's source of
  // truth for the assignment chip), then the route-derived crew leader. A
  // property can carry an assigned route name while primaryCrewLeader hasn't
  // resolved yet — without this branch the drawer reads Unassigned even though
  // the routing table correctly shows the route chip.
  const routeName = property.assignedRouteName || property.routeName || property.RouteName
  if (routeName) return routeName

  const primaryLabel = getCrewLabel(property.primaryCrewLeader)
  if (primaryLabel !== '-') return primaryLabel

  // Future-visit fallback: prefer a per-visit crew leader (most specific), then
  // a per-visit route name. #167 carries its route only on futureVisits[].routeName
  // (no top-level assignedRouteName, no crewLeaderName), so the routeName branch
  // is what keeps the drawer chip in sync with the table's Route·Day column.
  const futureVisits = property.futureVisits || []
  const leaderVisit = futureVisits.find(v => v && v.crewLeaderName)
  if (leaderVisit) return getCrewLabel({ crewLeaderName: leaderVisit.crewLeaderName })
  const routedVisit = futureVisits.find(v => v && v.routeName)
  if (routedVisit) return routedVisit.routeName

  return null
}

export const getPrimaryCrewName = (property = {}) => getPropertyRoute(property) || '-'

// Ribbon Margin KPI source. The headline % MUST equal the Profitability panel's
// "current margin" (both come from deriveProfitability — the authoritative,
// fully-costed, annualized P&L) so the two figures on screen agree. The raw
// aggregate kpis.margin is earned-rev-vs-labor-only and disagrees, so it's not
// used for the number. Margin is billing-scoped: when grouped, derive from the
// GROUP aggregate (profitabilitySidebarData), same as the panel. Overtime and
// Visits stay per-property. Spark = monthly cash trend from the same source.
// Ribbon Margin = REAL historic year-to-date margin (earned revenue vs actual
// labor), matching the Financials tab's "Net margin". This is intentionally NOT
// the Profitability tab's number — that tab projects costs to the full-year
// annual contract ("projected margin"); the ribbon and Financials report what
// actually happened. config is accepted for signature compatibility but the
// historic margin needs no projection knobs.
export const getRibbonMargin = (sidebarData = {}, profitabilitySidebarData = null) => {
  const source = profitabilitySidebarData && profitabilitySidebarData.billingGroup
    ? profitabilitySidebarData
    : sidebarData
  const financials = buildFinancialPeriodData(source, 'ytd')
  const monthlyRows = Array.isArray(financials.monthlyRows) ? financials.monthlyRows : []
  return {
    margin: safeNumber(financials.kpis && financials.kpis.margin),
    spark: monthlyRows.map(month => safeNumber(month.rev) + safeNumber(month.othRev) - safeNumber(month.cost))
  }
}

export const buildSidebarData = (selectedProperty, detailHistory, geoInsights) => {
  const property = selectedProperty || {}
  const visitRows = buildVisitChartRows(property, detailHistory.visits || [])
  const metricVisitRows = getMaintenanceMetricRows(visitRows)
  const monthlyRows = buildMonthlyRows(property, metricVisitRows)
  const serviceRows = buildServiceRows(property)
  const completedVisits = safeNumber(property.completedOccurrences)
  const annualVisits = safeNumber(property.targetAnnualVisits || property.annualOccurrences)
  const futureVisits = safeNumber(property.futureVisits?.length || property.futureTicketCount)
  const normalizedVisitHours = metricVisitRows.map(visit => visit.hrs).filter(hours => hours > 0)
  const normalizedAvgHours = averagePositive(normalizedVisitHours)
  const normalizedMedianHours = getMedian(normalizedVisitHours)
  const fallbackMedianHours = safeNumber(property.medianOnSiteHoursDisplay || property.medianOnSiteHours)
  const avgHours = normalizedAvgHours || safeNumber(property.avgOnSiteHoursDisplay || property.avgOnSiteHours)
  const estHours = safeNumber(property.estimatedHours)
  const prescribedManHours = safeNumber(property.prescribedManHours)
  const assignedCrewSize = safeNumber(property.assignedRouteCrewSize || property.internalEstimateCrewCount, 3) || 3
  const prescribedClockHours = prescribedManHours > 0 ? prescribedManHours / assignedCrewSize : 0
  // History-backed internal estimate (man-hour baseline ÷ median crew) —
  // preferred for display over the Aspire contract figure when present.
  const internalEstManHours = safeNumber(property.internalEstimatedManHours)
  const internalEstHours = internalEstManHours > 0 && assignedCrewSize > 0
    ? internalEstManHours / assignedCrewSize
    : safeNumber(property.internalEstimatedHours)
  const internalEstSource = property.internalEstimateSource || null
  const hasInternalEst = internalEstHours > 0 && internalEstSource && internalEstSource !== 'aspire'
  const normalizedDriveHours = averagePositive(metricVisitRows.map(getVisitDriveDurationHours))
  const rolledUpTotalActualHours = safeNumber(property.avgActualHours || property.hoursActual || property.avgOnSiteHoursDisplay || property.avgOnSiteHours)
  const rolledUpDriveActual = Math.max(0, rolledUpTotalActualHours - safeNumber(property.avgOnSiteHoursDisplay || property.avgOnSiteHours))
  const driveActual = normalizedDriveHours || rolledUpDriveActual
  const totalActualHours = avgHours + driveActual
  const driveExpected = safeNumber(property.driveExpectedHours, geoInsights?.yardRoute?.durationMinutes ? geoInsights.yardRoute.durationMinutes / 60 : safeNumber(geoInsights?.yardDriveMinutes) / 60)
  // Modeled ONE-WAY road miles yard→property (for drive mileage cost).
  const driveExpectedMiles = safeNumber(property.driveExpectedMiles, safeNumber(geoInsights?.yardRoadMiles))
  const totalEarnedRev = pickNumber(property, ['totalEarnedRevenue', 'totalRevenue'])
  const maintenanceEarnedRev = getFirstPresent(property, ['maintenanceEarnedRevenue']) === null
    ? totalEarnedRev
    : pickNumber(property, ['maintenanceEarnedRevenue'])
  const otherEarnedRev = pickNumber(property, ['otherEarnedRevenue'])
  const totalRev = getFirstPresent(property, ['totalEarnedRevenue', 'totalRevenue']) === null
    ? maintenanceEarnedRev + otherEarnedRev
    : totalEarnedRev
  const estLabor = pickNumber(property, ['estimatedLaborCost'])
  const actualLabor = pickNumber(property, ['maintenanceLaborCost', 'totalLaborCost'])
  const costVariance = getFirstPresent(property, ['costVariance']) === null
    ? estLabor - actualLabor
    : pickNumber(property, ['costVariance'])
  const netProfit = getFirstPresent(property, ['netProfit']) === null
    ? maintenanceEarnedRev - actualLabor
    : pickNumber(property, ['netProfit'])
  const crewName = getPrimaryCrewName(property)
  const scheduledDay = property.scheduledDays?.[0] || property.futureVisits?.[0]?.dayOfWeek || property.day || 'Unscheduled'

  return {
    property,
    propertyId: getAspirePropertyId(property),
    aspireUrl: getAspirePropertyUrl(property),
    address: [property.address1, property.address2].filter(Boolean).map(formatAddress).join(' '),
    crewName,
    scheduledDay,
    // Scenario context (set when the drawer is opened from the refine planner):
    // parked = scenario-assigned to a crew outside the active maintenance pool;
    // scenarioUnscheduled = on the scenario route but not placed on a day.
    parkedCrewName: property.parkedCrewName || null,
    parkedCrewInactive: property.parkedCrewInactive ?? null,
    parkedCrewDivision: property.parkedCrewDivision || null,
    scenarioUnscheduled: !!property.scenarioUnscheduled,
    companyChip: property.companyId ? `${property.companyName || 'Company'} #${property.companyId}` : null,
    visitRows,
    metricVisitRows,
    monthlyRows,
    serviceRows,
    kpis: {
      margin: safeNumber(property.profitMargin),
      netProfit,
      earnedRev: maintenanceEarnedRev,
      othRev: otherEarnedRev,
      totalRev,
      estLabor,
      actualLabor,
      costVariance,
      estHoursPerVisit: estHours,
      prescribedManHours,
      prescribedClockHours,
      prescribedCrewSize: assignedCrewSize,
      hasPrescribedHours: prescribedManHours > 0,
      internalEstHours,
      internalEstSource,
      internalEstVisitCount: safeNumber(property.internalEstimateVisitCount),
      hasInternalEst,
      avgHours,
      medianHours: normalizedMedianHours || fallbackMedianHours,
      normalizedMedianHours,
      normalizedVisitCount: normalizedVisitHours.length,
      fallbackMedianHours,
      estVariance: estHours - avgHours,
      otHours: safeNumber(property.totalOTHours),
      completedVisits,
      annualVisits,
      futureVisits,
      hoursActual: totalActualHours,
      driveActual,
      driveExpected,
      driveExpectedMiles
    }
  }
}

const sumMemberField = (members, field) => members.reduce((sum, member) => sum + safeNumber(member[field]), 0)

const getMemberAnnualRevenue = (member = {}) => {
  const targetAnnualVisits = safeNumber(member.targetAnnualVisits || member.annualOccurrences)
  const contractPricePerVisit = safeNumber(member.contractPricePerVisit)
  if (targetAnnualVisits > 0 && contractPricePerVisit > 0) {
    return targetAnnualVisits * contractPricePerVisit
  }
  return safeNumber(member.totalEstimatedRevenue)
}

export const buildBillingGroupProfitabilitySidebarData = (sidebarData = {}, billingGroupPayload = null) => {
  const group = billingGroupPayload?.group
  const members = Array.isArray(billingGroupPayload?.members) ? billingGroupPayload.members : []
  if (!group || members.length < 2) return sidebarData

  const selectedProperty = sidebarData.property || {}
  const memberIds = group.memberAspirePropertyIds || members.map(member => member.propertyId)
  const selectedPropertyId = getAspirePropertyId(selectedProperty)
  const annualVisits = members.reduce((sum, member) => {
    return sum + safeNumber(member.targetAnnualVisits || member.annualOccurrences)
  }, 0)
  const completedOccurrences = sumMemberField(members, 'completedOccurrences')
  const futureTicketCount = members.reduce((sum, member) => {
    return sum + safeNumber(member.futureTicketCount || member.futureVisits?.length)
  }, 0)
  const totalEstimatedRevenue = members.reduce((sum, member) => sum + getMemberAnnualRevenue(member), 0)
  const irrigationHours = sumMemberField(members, 'irrigationHours')
  const herbicideHours = sumMemberField(members, 'herbicideHours')
  const totalOnSiteHours = sumMemberField(members, 'totalOnSiteHours')
  const maintenanceHours = Math.max(0, totalOnSiteHours - irrigationHours - herbicideHours)
  const avgOnSiteHours = completedOccurrences > 0
    ? maintenanceHours / completedOccurrences
    : safeNumber(selectedProperty.avgOnSiteHoursDisplay || selectedProperty.avgOnSiteHours)
  const totalMaterialCost = sumMemberField(members, 'totalMaterialCost')
  const totalEquipmentCost = sumMemberField(members, 'totalEquipmentCost')
  const totalSubCost = sumMemberField(members, 'totalSubCost')
  const totalLaborCost = sumMemberField(members, 'totalLaborCost')
  const totalCost = totalLaborCost + totalMaterialCost + totalEquipmentCost + totalSubCost
  // Group pricing: prefer contracted (estimated) revenue, but fall back to real
  // EARNED revenue when members carry no estimate (no targetAnnualVisits×price
  // and no totalEstimatedRevenue). Without this fallback the ribbon Margin KPI
  // divides labor by ~0 estimated revenue and blows up to ~ -100000%.
  const totalEarnedRevenue = sumMemberField(members, 'totalEarnedRevenue')
  const marginRevenue = totalEstimatedRevenue > 0 ? totalEstimatedRevenue : totalEarnedRevenue
  const netProfit = marginRevenue - totalCost
  const profitMargin = marginRevenue > 0 ? (netProfit / marginRevenue) * 100 : 0

  // Combine every member's work tickets so the ticket-driven Financials tab can
  // sum revenue/labor across the whole group (otherwise it sees only the one
  // selected property's tickets — or none — and shows zeros).
  const groupWorkTickets = members.reduce((all, member) => {
    return Array.isArray(member.workTickets) ? all.concat(member.workTickets) : all
  }, [])

  const aggregateProperty = {
    ...selectedProperty,
    name: group.name,
    propertyId: selectedPropertyId,
    completedOccurrences,
    annualOccurrences: annualVisits,
    targetAnnualVisits: annualVisits,
    futureTicketCount,
    totalOnSiteHours,
    avgOnSiteHours,
    avgOnSiteHoursDisplay: avgOnSiteHours,
    medianOnSiteHours: avgOnSiteHours,
    medianOnSiteHoursDisplay: avgOnSiteHours,
    irrigationCompletedVisits: sumMemberField(members, 'irrigationCompletedVisits'),
    irrigationFutureVisits: sumMemberField(members, 'irrigationFutureVisits'),
    irrigationHours,
    irrigationLaborCost: sumMemberField(members, 'irrigationLaborCost'),
    herbicideCompletedVisits: sumMemberField(members, 'herbicideCompletedVisits'),
    herbicideFutureVisits: sumMemberField(members, 'herbicideFutureVisits'),
    herbicideHours,
    herbicideLaborCost: sumMemberField(members, 'herbicideLaborCost'),
    totalLaborCost,
    maintenanceLaborCost: Math.max(0, totalLaborCost - sumMemberField(members, 'irrigationLaborCost') - sumMemberField(members, 'herbicideLaborCost')),
    totalMaterialCost,
    totalEquipmentCost,
    totalSubCost,
    totalActualSpend: totalCost,
    totalEstimatedRevenue,
    totalEarnedRevenue,
    contractPricePerVisit: annualVisits > 0 ? totalEstimatedRevenue / annualVisits : 0,
    // Group-wide tickets drive the Financials tab; the no-ticket fallback there
    // reads these aggregate revenue/labor fields, so set them too.
    workTickets: groupWorkTickets,
    maintenanceEarnedRevenue: sumMemberField(members, 'maintenanceEarnedRevenue'),
    otherEarnedRevenue: sumMemberField(members, 'otherEarnedRevenue'),
    estimatedLaborCost: sumMemberField(members, 'estimatedLaborCost'),
    // All-service Financials needs the maintenance/other split + other labor.
    maintenanceEstimatedRevenue: sumMemberField(members, 'maintenanceEstimatedRevenue'),
    otherEstimatedRevenue: sumMemberField(members, 'otherEstimatedRevenue'),
    otherLaborCost: sumMemberField(members, 'otherLaborCost'),
    netProfit,
    profitMargin
  }

  return {
    ...buildSidebarData(aggregateProperty, { visits: [] }, {}),
    billingGroup: {
      id: group._id,
      name: group.name,
      notes: group.notes || '',
      selectedPropertyId,
      memberAspirePropertyIds: memberIds,
      members: members.map(member => ({
        propertyId: getAspirePropertyId(member),
        name: member.name || `Property #${getAspirePropertyId(member)}`,
        address1: member.address1 || ''
      }))
    }
  }
}

export const getPropertyCoordinates = (row = {}) => {
  const property = row || {}
  const lat = Number(property.latitude ?? property.location?.coordinates?.[1])
  const lng = Number(property.longitude ?? property.location?.coordinates?.[0])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

export const formatMiles = (miles) => {
  const value = Number(miles)
  if (!Number.isFinite(value)) return '-'
  return `${value.toFixed(value < 10 ? 1 : 0)} mi`
}

const estimateRoadMiles = (straightLineMiles) => {
  const miles = Number(straightLineMiles)
  if (!Number.isFinite(miles)) return null
  return miles <= 0 ? 0 : miles * 1.25
}

const estimateDriveMinutes = (roadMiles) => {
  const miles = Number(roadMiles)
  if (!Number.isFinite(miles)) return null
  if (miles <= 0) return 0
  return Math.max(4, Math.round((miles / 32) * 60))
}

export const buildGeoInsights = (selectedProperty, allRows = []) => {
  const selectedCoords = getPropertyCoordinates(selectedProperty)
  if (!selectedCoords) {
    return {
      hasCoordinates: false,
      selectedCoords: null,
      nearestSite: null,
      sitesWithinFiveMiles: [],
      sitesWithinThreeMiles: [],
      sitesWithinOneMile: [],
      avgDistanceWithinFiveMiles: null,
      medianDistanceWithinFiveMiles: null,
      yardStraightMiles: null,
      yardRoadMiles: null,
      yardDriveMinutes: null
    }
  }

  const selectedId = String(getAspirePropertyId(selectedProperty) || selectedProperty?._id || '')
  const nearbySites = allRows
    .map(row => {
      const coords = getPropertyCoordinates(row)
      const rowId = String(getAspirePropertyId(row) || row?._id || '')
      if (!coords || (selectedId && rowId === selectedId)) return null
      const distanceMiles = haversineMiles(selectedCoords.lat, selectedCoords.lng, coords.lat, coords.lng)
      return { ...row, distanceMiles }
    })
    .filter(Boolean)
    .sort((a, b) => a.distanceMiles - b.distanceMiles)

  const yardStraightMiles = haversineMiles(HMI_YARD.lat, HMI_YARD.lng, selectedCoords.lat, selectedCoords.lng)
  const yardRoadMiles = estimateRoadMiles(yardStraightMiles)
  const sitesWithinFiveMiles = nearbySites.filter(site => site.distanceMiles <= 5)
  const withinFiveDistances = sitesWithinFiveMiles.map(site => site.distanceMiles).sort((a, b) => a - b)
  const avgDistanceWithinFiveMiles = withinFiveDistances.length
    ? withinFiveDistances.reduce((sum, distance) => sum + distance, 0) / withinFiveDistances.length
    : null
  const medianDistanceWithinFiveMiles = withinFiveDistances.length
    ? withinFiveDistances[Math.floor(withinFiveDistances.length / 2)]
    : null

  return {
    hasCoordinates: true,
    selectedCoords,
    nearestSite: nearbySites[0] || null,
    sitesWithinFiveMiles,
    sitesWithinThreeMiles: nearbySites.filter(site => site.distanceMiles <= 3),
    sitesWithinOneMile: nearbySites.filter(site => site.distanceMiles <= 1),
    avgDistanceWithinFiveMiles,
    medianDistanceWithinFiveMiles,
    yardStraightMiles,
    yardRoadMiles,
    yardDriveMinutes: estimateDriveMinutes(yardRoadMiles)
  }
}

export const getVisitHistoryRows = (property = {}) => {
  const row = property || {}
  return getFirstPresent(row, [
    'visitHistory',
    'completedVisits',
    'recentVisits',
    'pastVisits',
    'visits'
  ]) || []
}

export const getServiceDetail = (property = {}, service) => {
  const prefix = service.toLowerCase()
  const displayName = service === 'Irrigation' ? 'Irrigation' : 'Herbicide'
  const completed = Number(property?.[`${prefix}CompletedVisits`] || 0)
  const future = Number(property?.[`${prefix}FutureVisits`] || 0)
  const hours = property?.[`${prefix}Hours`]
  // Actual man-hours (server-stored, NOT ÷crew) for the $/man-hr rate.
  const manHours = Number(property?.[`${prefix}ManHours`] || 0)
  const cost = getFirstPresent(property, [
    `${prefix}Cost`,
    `${prefix}LaborCost`,
    `${prefix}TotalCost`,
    `${prefix}TotalLaborCost`,
    `total${displayName}Cost`,
    `total${displayName}LaborCost`
  ])

  return {
    name: displayName,
    tickets: completed + future,
    completed,
    future,
    hours,
    manHours,
    cost
  }
}

export const normalizeRoutingRow = (row = {}) => {
  const estimatedHours = Number(row.estimatedHours || 0)
  const prescribedManHours = row.prescribedManHours != null ? Number(row.prescribedManHours) : null
  const internalEstHoursDisplay = Number(row.internalEstimatedHours || 0)
  const avgOnSiteHoursDisplay = Number(row.avgOnSiteHours || row.avgActualHours || 0)
  const medianOnSiteHoursDisplay = Number(row.medianOnSiteHours || row.medianActualHours || 0)
  const companyId = row.companyId || row.CompanyID || null
  const companyName = row.companyName || row.CompanyName || ''
  const totalEarnedRevenue = pickNumber(row, ['totalEarnedRevenue', 'totalRevenue'])
  const contractPricePerVisit = Number(row.contractPricePerVisit || 0)
  const annualTicketCount = Number(row.annualOccurrences || 0)
  const targetAnnualVisits = Number(row.targetAnnualVisits || annualTicketCount || 0)
  const annualVisitsDisplay = targetAnnualVisits || annualTicketCount
  const completedMtcVisits = Number(row.completedOccurrences || 0)
  const herbicideCompletedVisits = Number(row.herbicideCompletedVisits || 0)
  const irrigationCompletedVisits = Number(row.irrigationCompletedVisits || 0)
  const herbicideAnnualVisits = herbicideCompletedVisits + Number(row.herbicideFutureVisits || 0)
  const irrigationAnnualVisits = irrigationCompletedVisits + Number(row.irrigationFutureVisits || 0)
  const completedTotalVisits = completedMtcVisits + herbicideCompletedVisits + irrigationCompletedVisits
  const annualTotalVisits = annualVisitsDisplay + herbicideAnnualVisits + irrigationAnnualVisits
  const estimatedRevenueFromPlan = contractPricePerVisit > 0 && targetAnnualVisits > 0 ? contractPricePerVisit * targetAnnualVisits : 0
  const totalEstimatedRevenue = Number(estimatedRevenueFromPlan || row.totalEstimatedRevenue || 0)

  const maintenanceEarnedRevenue = getFirstPresent(row, ['maintenanceEarnedRevenue']) === null
    ? totalEarnedRevenue
    : pickNumber(row, ['maintenanceEarnedRevenue'])
  const maintenanceLaborCost = pickNumber(row, ['maintenanceLaborCost', 'totalLaborCost'])
  const netProfit = getFirstPresent(row, ['netProfit']) === null
    ? maintenanceEarnedRevenue - maintenanceLaborCost
    : pickNumber(row, ['netProfit'])

  return {
    ...row,
    companyId,
    companyName,
    prescribedManHours,
    companyLabel: companyId
      ? `${companyName || 'Company'} #${companyId}`
      : 'No company',
    estimatedHours,
    internalEstHoursDisplay,
    avgOnSiteHoursDisplay,
    medianOnSiteHoursDisplay,
    onSiteVariance: estimatedHours - avgOnSiteHoursDisplay,
    completedMtcVisits,
    completedTotalVisits,
    herbicideCompletedVisits,
    irrigationCompletedVisits,
    herbicideAnnualVisits,
    irrigationAnnualVisits,
    annualTicketCount,
    targetAnnualVisits,
    annualVisitsDisplay,
    annualTotalVisits,
    estimatedRevenueFromPlan,
    contractPricePerVisit,
    totalEstimatedRevenue,
    maintenanceEarnedRevenue,
    maintenanceLaborCost,
    netProfit,
    totalEarnedRevenue
  }
}

export const getAspirePropertyId = (row = {}) => {
  const property = row || {}
  return property.aspirePropertyId || property.propertyId || property.PropertyID || null
}

export const getAspirePropertyUrl = (row = {}) => {
  const propertyId = getAspirePropertyId(row)
  return propertyId ? `https://cloud.youraspire.com/app/properties/overview/${propertyId}` : null
}

export const isRowProblematic = (row) => {
  return row.netProfit < 0 || row.onSiteVariance < -2 || row.profitMargin < -50
}

// ─── Profitability calculator ──────────────────────────────────────────────
// Pure derivation powering the Profitability drawer tab. Takes the object from
// buildSidebarData() and returns a read-only result driving the 3-card panel.
// Math is locked per spec 2026-06-22-property-profitability-calculator (client).

// Company-wide target margin (ratio). Single constant for v1 — structured so it
// can later become a per-division lookup.
export const PROFIT_TARGET = 0.50
// Below this completed-visit count, history is too thin → 'sparse' state wins.
export const SPARSE_MIN_VISITS = 8
// Recommendation cuts on-site hours this far below the MEDIAN before suggesting
// any price increase (median, not avg — actual hours inflate to fill the day).
export const HOURS_CUT_FROM_MEDIAN = 0.25

// Assumed-minimum recurring cost for a CONTRACTED service that has little/no
// actual cost yet (hasHerbicideContract / hasIrrigationContract). Projects a
// floor cost = cadenceVisits/yr × hoursPerVisit × loadedRate so a contractual
// obligation isn't invisible and the margin isn't overstated. Tunable.
export const CONTRACT_MIN_DEFAULTS = {
  herbicide: { visitsPerYear: 2, defaultHoursPerVisit: 0.5 },
  irrigation: { visitsPerYear: 6, defaultHoursPerVisit: 0.5 }
}
// Fallback loaded labor rate ($/man-hour) when maintenance has no cost/hours
// basis to derive one from.
export const DEFAULT_LOADED_RATE_PER_HOUR = 90

// Drive cost inputs (Phase 1 constants — become global ProfitabilitySettings in
// Phase 2). Drive uses the modeled OSRM one-way yard→property time/miles.
export const DRIVE_LABOR_RATE_PER_HOUR = 32 // planning burden rate ($/man-hr)
export const MILEAGE_RATE_PER_MILE = 0.655  // IRS standard rate
export const DRIVE_ROUND_TRIP_FACTOR = 1    // one-way leg attributed per visit

const SERVICE_KEYS = [
  { key: 'irrigation', name: 'Irrigation' },
  { key: 'herbicide', name: 'Herbicide' },
  { key: 'maintenance', name: 'Maintenance' }
]

const roundTo = (value, step) => Math.round(value / step) * step

/**
 * Derive the profitability panel model from a sidebar-data object.
 * @param {object} sidebarData - output of buildSidebarData()
 * @param {object} [config] - { target, sparseMinVisits, burdenRate, mileageRate,
 *   driveRoundTripFactor, driveOverride: { hours, miles, source }, hoursBasis,
 *   overrides: { pricePerVisit, hoursPerVisit } }. The drive knobs
 *   (burdenRate/mileageRate/driveRoundTripFactor) fall back to the module
 *   constants when absent; driveRoundTripFactor scales only the yard estimate,
 *   not a route-derived driveOverride. overrides drive the interactive what-if
 *   sliders (recompute revenue + maintenance labor live).
 * @returns {object} panel model. Includes `baseline` (current price/hours to
 *   seed sliders) and `recommended` (efficiency-first target: cut hours toward
 *   25%-below-median, raise price only for the remaining gap, never lower price
 *   or add hours; { pricePerVisit, hoursPerVisit, priceChanged, hoursChanged }).
 */
export const deriveProfitability = (sidebarData = {}, config = {}) => {
  const target = config.target != null ? config.target : PROFIT_TARGET
  const sparseMin = config.sparseMinVisits != null ? config.sparseMinVisits : SPARSE_MIN_VISITS
  const burdenRate = config.burdenRate != null ? config.burdenRate : DRIVE_LABOR_RATE_PER_HOUR
  const mileageRate = config.mileageRate != null ? config.mileageRate : MILEAGE_RATE_PER_MILE
  const driveFactor = config.driveRoundTripFactor != null ? config.driveRoundTripFactor : DRIVE_ROUND_TRIP_FACTOR

  const property = sidebarData.property || {}
  const kpis = sidebarData.kpis || {}
  const serviceRows = sidebarData.serviceRows || []

  const completed = safeNumber(kpis.completedVisits)
  const targetPct = Math.round(target * 100)

  // ── Annual P&L (Card 1). Revenue anchored to current contract value. ──
  // contractPricePerVisit × annual visits is the cleanest "what we're paid".
  const contractPricePerVisit = safeNumber(property.contractPricePerVisit)
  const annualVisits = safeNumber(kpis.annualVisits)
  const baseRevenue = contractPricePerVisit > 0 && annualVisits > 0
    ? contractPricePerVisit * annualVisits
    : safeNumber(property.totalEstimatedRevenue) || safeNumber(kpis.totalRev)

  // ── Time-basis fix: revenue above is FULL-YEAR (annual contract / annualized
  // earned), but the cost rollups below are YTD actuals (completed visits only).
  // Project ALL costs to full year using ONE property-level factor — the same
  // visit ratio that annualizes revenue — so cost and revenue share a basis and
  // the margin reconciles with the Financials tab. The factor uses the
  // authoritative annual visit count (annualOccurrences / targetAnnualVisits),
  // NOT the flaky count of currently-scheduled future tickets. ──
  const findRow = (name) => serviceRows.find(r => r.name === name) || {}
  // Project each service to full year by ITS OWN completed→annual ratio. The
  // services run on different cadences and have different clock-data coverage,
  // so a single blended factor (maintenance annual ÷ all-service completed)
  // under-projects every line. Per-service: serviceAnnualVisits / serviceDone.
  const serviceAnnualFactor = (row, serviceAnnualVisits) => {
    const done = safeNumber(row.completed)
    const annual = safeNumber(serviceAnnualVisits)
    return (annual > 0 && done > 0 && annual > done) ? annual / done : 1
  }
  // Maintenance annual visits = the contract target (kpis.annualVisits). Irrig/
  // herb annual = their own completed + future tickets (their real cadence).
  const irrAnnualVisits = safeNumber(findRow('Irrigation').completed) + safeNumber(findRow('Irrigation').future)
  const herbAnnualVisits = safeNumber(findRow('Herbicide').completed) + safeNumber(findRow('Herbicide').future)
  // `annualFactor` stays the MAINTENANCE factor — downstream direct-cost and
  // clock-loaded-rate math is maintenance-side and reuses it. The maintenance
  // done-count is the row's completed, falling back to the top-level completed
  // visit count (maintenance is the dominant service) when the row omits it.
  const maintCompleted = safeNumber(findRow('Maintenance').completed) || completed
  const annualFactor = serviceAnnualFactor({ completed: maintCompleted }, annualVisits)
  const irrFactor = serviceAnnualFactor(findRow('Irrigation'), irrAnnualVisits)
  const herbFactor = serviceAnnualFactor(findRow('Herbicide'), herbAnnualVisits)
  const actualIrrigationCost = safeNumber(findRow('Irrigation').cost) * irrFactor
  const actualHerbicideCost = safeNumber(findRow('Herbicide').cost) * herbFactor
  // Actual maintenance labor projected to full year — the baseline cost for the
  // 'actual' hours basis (and the source of the clock-hour loaded rate below).
  const actualMaintenanceCost = safeNumber(findRow('Maintenance').cost) * annualFactor
  const directCost = (safeNumber(property.totalMaterialCost) +
    safeNumber(property.totalEquipmentCost) + safeNumber(property.totalSubCost)) * annualFactor

  // ── Contracted-service assumed minimum ──
  // A property contracted for irrigation/herbicide carries a recurring cost even
  // when little/no actual cost was booked this year. Project a floor so the
  // margin isn't overstated: cadence visits/yr × hours/visit × loaded $/hr, and
  // take max(actual, floor). The loaded rate reuses maintenance's cost/hour
  // (the only service with a reliable basis), else a config default. Lines whose
  // cost comes from the floor are tagged `assumed` for the panel.
  const maintRow = findRow('Maintenance')
  const loadedRatePerHour = safeNumber(maintRow.hours) > 0 && safeNumber(maintRow.cost) > 0
    ? safeNumber(maintRow.cost) / safeNumber(maintRow.hours)
    : DEFAULT_LOADED_RATE_PER_HOUR
  const assumedMinFor = (rowName, defaults) => {
    const row = findRow(rowName)
    const completedService = safeNumber(row.completed)
    const hoursPerVisit = completedService > 0 && safeNumber(row.hours) > 0
      ? safeNumber(row.hours) / completedService
      : defaults.defaultHoursPerVisit
    return defaults.visitsPerYear * hoursPerVisit * loadedRatePerHour
  }
  const applyContractFloor = (actualCost, contracted, rowName, defaults) => {
    if (!contracted) return { cost: actualCost, assumed: false }
    const floor = assumedMinFor(rowName, defaults)
    return floor > actualCost ? { cost: floor, assumed: true } : { cost: actualCost, assumed: false }
  }
  const irrigationProjected = applyContractFloor(
    actualIrrigationCost, !!property.hasIrrigationContract, 'Irrigation', CONTRACT_MIN_DEFAULTS.irrigation)
  const herbicideProjected = applyContractFloor(
    actualHerbicideCost, !!property.hasHerbicideContract, 'Herbicide', CONTRACT_MIN_DEFAULTS.herbicide)
  const irrigationCost = irrigationProjected.cost
  const herbicideCost = herbicideProjected.cost

  // ── Hours basis: which per-visit on-site hours drive the slider + maintenance
  // cost. 'actual' = avg actual on-site (default, unchanged); 'internal' = the
  // trimmed-mean internal estimate (outlier-resistant); 'prescribed' = the
  // manager-set prescribed clock hours. All three are already computed in kpis. ──
  const actualHours = safeNumber(kpis.avgHours)
  const internalHours = safeNumber(kpis.internalEstHours)
  const prescribedHours = safeNumber(kpis.prescribedClockHours)
  const basisHours = { actual: actualHours, internal: internalHours, prescribed: prescribedHours }
  // Which bases have usable data (>0) — drives the chip row in the panel.
  const availableBases = ['actual', 'internal', 'prescribed'].filter(b => basisHours[b] > 0)
  const requestedBasis = config.hoursBasis || 'actual'
  const hoursBasis = basisHours[requestedBasis] > 0 ? requestedBasis : 'actual'

  // Clock-hour loaded labor rate from ACTUALS: actual maintenance $ ÷ actual
  // maintenance clock-hours (avg clock hours/visit × completed). Stays in
  // clock-hours so no crew-size term enters the cost math (the rate already
  // bakes in crew). Falls back to a per-clock-hour default when no basis.
  const actualMaintClockHours = actualHours * completed
  const clockLoadedRate = actualMaintClockHours > 0
    ? actualMaintenanceCost / (actualMaintClockHours * annualFactor)
    : DEFAULT_LOADED_RATE_PER_HOUR

  // Baseline per-visit levers — what the interactive sliders start from. Price
  // falls back to revenue/visits when no contract price.
  const baseHoursPerVisit = basisHours[hoursBasis] || actualHours
  // Maintenance cost follows the basis: 'actual' keeps the real labor dollars;
  // 'internal'/'prescribed' recompute as hours × clock rate × annual visits so
  // hours and dollars share one basis.
  const baseMaintenanceCost = hoursBasis === 'actual'
    ? actualMaintenanceCost
    : baseHoursPerVisit * clockLoadedRate * annualVisits
  const basePricePerVisit = contractPricePerVisit > 0
    ? contractPricePerVisit
    : (annualVisits > 0 ? baseRevenue / annualVisits : 0)

  // Crew-size alert: the internal/prescribed bases derive from man-hr↔clock
  // conversions that assume a typical crew; a larger crew may skew them.
  const crewSize = safeNumber(property.assignedRouteCrewSize || property.internalEstimateCrewCount)
  const crewSizeAlert = crewSize > 3 ? crewSize : null

  // ── Drive cost (labor + mileage) ──
  // Previously ignored. Drive labor: the whole crew is paid to drive, so
  // hours × crew × rate. Mileage: one truck, miles × $/mile. Source is the
  // modeled OSRM one-way yard→property leg (Aspire's per-ticket drive is
  // route-end-of-day skewed). Drive is independent of on-site time, so it is
  // held fixed when the on-site-hours slider moves.
  const driveCrew = crewSize > 0 ? crewSize : 3
  // A route-derived inter-stop drive may override the yard estimate. The yard
  // values are kept as a reference for a used-vs-reference UI comparison.
  const ovDrive = config.driveOverride || null
  const yardHoursPerVisit = safeNumber(kpis.driveExpected) * driveFactor
  const yardMilesPerVisit = safeNumber(kpis.driveExpectedMiles) * driveFactor
  const driveHoursPerVisit = ovDrive ? safeNumber(ovDrive.hours) : yardHoursPerVisit
  const driveMilesPerVisit = ovDrive ? safeNumber(ovDrive.miles) : yardMilesPerVisit
  const driveSource = ovDrive ? (ovDrive.source || 'route') : 'yard'
  const driveLaborAnnual = driveHoursPerVisit * driveCrew * burdenRate * annualVisits
  const driveMileageAnnual = driveMilesPerVisit * mileageRate * annualVisits
  const driveCost = driveLaborAnnual + driveMileageAnnual

  // Baseline margin (ignores overrides) — drives the hold-vs-act recommendation.
  const baseTotalCost0 = baseMaintenanceCost + irrigationCost + herbicideCost + directCost + driveCost
  const baseMargin0 = baseRevenue > 0 ? (baseRevenue - baseTotalCost0) / baseRevenue : 0

  // ── Interactive overrides (what-if sliders). When present, recompute revenue
  // from the chosen price and maintenance labor scaled by the chosen hours.
  // Other service/material costs are held fixed. ──
  const ov = config.overrides || null
  const interactive = !!ov
  const ovPrice = ov && ov.pricePerVisit != null ? safeNumber(ov.pricePerVisit) : null
  const ovHours = ov && ov.hoursPerVisit != null ? safeNumber(ov.hoursPerVisit) : null

  const revenue = ovPrice != null && annualVisits > 0
    ? ovPrice * annualVisits
    : (ovPrice != null ? ovPrice * Math.max(1, completed) : baseRevenue)
  const maintenanceCost = ovHours != null && baseHoursPerVisit > 0
    ? baseMaintenanceCost * (ovHours / baseHoursPerVisit)
    : baseMaintenanceCost

  // Cost lines carry an optional third tuple element: { assumed: true } when the
  // cost came from a contracted-service floor rather than actuals. Tuple stays
  // [label, cost, meta?] so existing [label, cost] consumers are unaffected.
  const costLines = [
    ['Irrigation labor', irrigationCost, irrigationProjected.assumed ? { assumed: true } : undefined],
    ['Herbicide labor', herbicideCost, herbicideProjected.assumed ? { assumed: true } : undefined],
    ['Maintenance labor', maintenanceCost],
    ['Materials · equip · sub', directCost],
    ...(driveCost > 0 ? [['Drive · labor + mileage', driveCost, { drive: true }]] : [])
  ]
  const usesAssumedMinimums = irrigationProjected.assumed || herbicideProjected.assumed
  const totalCost = costLines.reduce((sum, [, cost]) => sum + cost, 0)
  const profit = revenue - totalCost
  const margin = revenue > 0 ? profit / revenue : 0
  const marginPct = Math.round(margin * 100)
  const gapPts = Math.abs(targetPct - marginPct)

  // ── State ──
  let state
  if (completed < sparseMin) state = 'sparse'
  else if (margin >= target) state = 'healthy'
  else state = 'below'

  const pnl = {
    revenue,
    // Preserve the optional meta tuple (e.g. { assumed: true }) on each line.
    lines: costLines.map(([label, cost, meta]) => meta ? [label, -cost, meta] : [label, -cost]),
    profit,
    marginPct,
    usesAssumedMinimums
  }

  // ── Card 3 rates: loaded cost per MAN-hour, held to (1 - target) of price. ──
  // Uses the server-stored ACTUAL man-hours per service (serviceRows.manHours),
  // NOT serviceRows.hours which is clock-hours (man-hrs ÷ crew). Dividing labor $
  // by true man-hours gives a real loaded burden (~$23/man-hr for #20), not a
  // crew-hour rate.
  const rateFor = (rowName, costKey) => {
    const row = findRow(rowName)
    const manHours = safeNumber(row.manHours)
    const cost = safeNumber(row.cost)
    if (manHours <= 0 || cost <= 0) {
      return { key: costKey, name: rowName, rate: null }
    }
    const loadedPerManHour = cost / manHours
    const rate = loadedPerManHour / (1 - target)
    return { key: costKey, name: rowName, rate, manHours, cost }
  }
  // Maintenance rate reacts to the what-if sliders. Cost and man-hours stay on
  // the SAME (annualized) basis: live (override-scaled) maintenance cost ÷ live
  // annual man-hours = actual maintenance man-hours × annualFactor, scaled by the
  // slider's hours ratio (chosen ÷ baseline) so dragging hours moves the rate.
  const actualMaintManHrsAnnual = safeNumber(findRow('Maintenance').manHours) * annualFactor
  const hoursRatio = baseHoursPerVisit > 0 && ovHours != null ? ovHours / baseHoursPerVisit : 1
  const maintLiveManHours = actualMaintManHrsAnnual * hoursRatio
  const maintLiveRate = maintLiveManHours > 0 && maintenanceCost > 0
    ? (maintenanceCost / maintLiveManHours) / (1 - target)
    : null
  const maintenanceRate = {
    key: 'maintenance',
    name: 'Maintenance',
    rate: maintLiveRate,
    cost: maintenanceCost,
    manHours: maintLiveManHours,
    perVisit: maintLiveRate != null && annualVisits > 0
      ? maintenanceCost / annualVisits / (1 - target)
      : (maintLiveRate != null && completed > 0 ? maintenanceCost / completed / (1 - target) : null)
  }
  const rates = state === 'sparse'
    ? SERVICE_KEYS.map(s => ({ key: s.key, name: s.name, rate: null }))
    : [
        { ...rateFor('Irrigation', 'irrigation'), ok: state === 'healthy' },
        { ...rateFor('Herbicide', 'herbicide'), ok: state === 'healthy' },
        { ...maintenanceRate, ok: state === 'healthy' }
      ]

  // ── Recommendation: efficiency-FIRST, price only if needed. ──
  // Real-world lever asymmetry: price is sticky/annual and must never go DOWN;
  // hours are operational and can be tightened next visit. So: if at/above
  // target, recommend nothing (both stick). If below, cut hours toward a floor
  // (25% below MEDIAN on-site — median because actuals inflate to fill the day);
  // only raise price for whatever gap the hours cut can't close. Never lowers
  // price, never adds hours.
  const medianHours = safeNumber(kpis.medianHours) || baseHoursPerVisit
  const hoursFloor = medianHours > 0 ? medianHours * (1 - HOURS_CUT_FROM_MEDIAN) : baseHoursPerVisit
  let recommended = null
  if (baseRevenue > 0 && baseHoursPerVisit > 0) {
    const otherCost = irrigationCost + herbicideCost + directCost + driveCost
    if (baseMargin0 >= target) {
      // At/above target — hold. Price sticks, hours stick.
      recommended = {
        pricePerVisit: basePricePerVisit,
        hoursPerVisit: baseHoursPerVisit,
        priceChanged: false,
        hoursChanged: false
      }
    } else {
      // Step 1: cut hours toward the floor (never below; never above current).
      const recHours = roundTo(Math.min(baseHoursPerVisit, Math.max(hoursFloor, 0.1)), 0.1)
      const costAtRecHours = baseMaintenanceCost * (recHours / baseHoursPerVisit) + otherCost
      const marginAtRecHours = baseRevenue > 0 ? (baseRevenue - costAtRecHours) / baseRevenue : 0
      // Step 2: if hours alone still miss, raise price to cover the remainder.
      let recPrice = basePricePerVisit
      if (marginAtRecHours < target) {
        const neededRevenue = costAtRecHours / (1 - target)
        const perVisit = annualVisits > 0 ? neededRevenue / annualVisits : neededRevenue / Math.max(1, completed)
        recPrice = roundTo(Math.max(basePricePerVisit, perVisit), 5) // never below current
      }
      recommended = {
        pricePerVisit: recPrice,
        hoursPerVisit: recHours,
        priceChanged: recPrice > basePricePerVisit,
        hoursChanged: recHours < baseHoursPerVisit
      }
    }
  }

  const result = {
    state,
    interactive,
    margin: marginPct,
    target: targetPct,
    gapPts,
    pnl,
    rates,
    completed,
    // Baseline + recommended values that seed the interactive sliders.
    baseline: { pricePerVisit: basePricePerVisit, hoursPerVisit: baseHoursPerVisit, annualVisits },
    recommended,
    levers: null,
    // Hours-basis selector state for the panel chips.
    hoursBasis,
    availableBases,
    basisHours: {
      actual: actualHours || null,
      internal: internalHours || null,
      prescribed: prescribedHours || null
    },
    crewSizeAlert,
    // Drive cost breakdown for the panel (one-way OSRM basis).
    drive: driveCost > 0
      ? {
          cost: driveCost,
          laborAnnual: driveLaborAnnual,
          mileageAnnual: driveMileageAnnual,
          hoursPerVisit: driveHoursPerVisit,
          milesPerVisit: driveMilesPerVisit,
          crew: driveCrew,
          source: driveSource,
          referenceYardHours: yardHoursPerVisit,
          referenceYardMiles: yardMilesPerVisit
        }
      : null
  }

  // ── Path-to-target levers, derived from the efficiency-first `recommended`
  // (single source of truth). Hours cut first; price only rises when it must,
  // and NEVER falls. Suppressed while driving the interactive sliders. ──
  if (!interactive && state === 'below' && recommended) {
    const priceFrom = basePricePerVisit
    const priceTo = recommended.pricePerVisit
    const hoursFrom = baseHoursPerVisit
    const hoursTo = recommended.hoursPerVisit
    result.levers = {
      price: {
        from: priceFrom,
        to: priceTo,
        pct: priceFrom > 0 ? Math.round((priceTo / priceFrom - 1) * 100) : 0,
        changed: recommended.priceChanged,
        unit: '/ visit'
      },
      time: {
        from: hoursFrom,
        to: hoursTo,
        changed: recommended.hoursChanged,
        unit: '/ visit'
      }
    }
  }

  return result
}

// Pull billing-group overlap conflicts out of a failed save error. The server
// returns 409 { code: 'BILLING_GROUP_OVERLAP', conflicts: [...] } when a
// member already belongs to another active group. Returns the conflicts array,
// or null if this error is not an overlap conflict.
export const getBillingGroupOverlapConflicts = (err) => {
  const data = err && err.response && err.response.data
  if (data && data.code === 'BILLING_GROUP_OVERLAP' && Array.isArray(data.conflicts)) {
    return data.conflicts
  }
  return null
}

// Persist a billing group: PUT when it already has an _id (edit existing),
// POST otherwise (create new). `force` threads the overlap-move flag into the
// request body. Returns the axios response. Pass the axios instance + base url
// so this stays free of import cycles with host components.
export const saveBillingGroup = (axios, url, billingGroup, force = false) => {
  const { _id, ...fields } = billingGroup || {}
  const body = { ...fields, force: !!force }
  return _id
    ? axios.put(`${url}/api/aspire/routing/billing-groups/${_id}`, body)
    : axios.post(`${url}/api/aspire/routing/billing-groups`, body)
}
